// src/store/Slices/FuturesTradingSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../API/api';
// عدّل المسار إن لزم حسب مشروعك
import { fetchFuturesBalances } from './assetSlice';

export const createFutureTrade = createAsyncThunk(
  'futures/createFutureTrade',
  async (tradeData, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.post('/futures/trade', { ...tradeData });

      // ✅ حدّث الرصيد ثم الأوامر المفتوحة — ننتظر اكتمال تحديث الرصيد لضمان انعكاسه في OrderForm
      await dispatch(fetchFuturesBalances()).unwrap();
      await dispatch(fetchOpenFutureTrades()).unwrap();

      return res.data; // { trade }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create future trade');
    }
  }
);

export const closeFutureTrade = createAsyncThunk(
  'futures/closeFutureTrade',
  async ({ trade_id }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.post(`/futures/close/${trade_id}`, {});

      // ✅ بعد الإغلاق: حدّث الرصيد + القوائم المفتوحة + التاريخ (بالترتيب)
      await dispatch(fetchFuturesBalances()).unwrap();
      await dispatch(fetchOpenFutureTrades()).unwrap();
      await dispatch(fetchFutureTradeHistory()).unwrap();

      return res.data.trade; // updated trade
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to close future trade');
    }
  }
);

export const cancelFutureTrade = createAsyncThunk(
  'futures/cancelFutureTrade',
  async ({ trade_id }, { rejectWithValue, dispatch }) => {
    try {
      const res = await api.post(`/futures/cancel/${trade_id}`, {});

      // ✅ قد تتحرر سيولة بعد الإلغاء -> حدّث الرصيد ثم الأوامر المفتوحة
      await dispatch(fetchFuturesBalances()).unwrap();
      await dispatch(fetchOpenFutureTrades()).unwrap();

      return { trade_id, ...res.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to cancel future trade');
    }
  }
);

export const fetchOpenFutureTrades = createAsyncThunk(
  'futures/fetchOpenFutureTrades',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { data: user } = getState().user;
      const res = await api.get(`/futures/open/${user._id}`);
      return res.data.trades || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch open future trades');
    }
  }
);

export const fetchFutureTradeHistory = createAsyncThunk(
  'futures/fetchFutureTradeHistory',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { data: user } = getState().user;
      const res = await api.get(`/futures/history/${user._id}`);
      return res.data.history || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch future trade history');
    }
  }
);

const futuresSlice = createSlice({
  name: 'futures',
  initialState: {
    openTrades: [],
    tradeHistory: [],
    selectedCoin: 'BTCUSDT',
    tradeHistoryLoading: false,
    openOrdersLoading: false,
    error: null,
    successMessage: null,
  },
  reducers: {
    updateOrderStatus: (state, action) => {
      const { trade_id, status, symbol, open_price, liquidation_price, close_price, pnl } = action.payload;
      const idx = state.openTrades.findIndex((t) => t._id === trade_id);

      if (status === 'Filled') {
        if (idx !== -1) {
          state.openTrades[idx] = {
            ...state.openTrades[idx],
            status,
            open_price,
            liquidation_price,
          };
        }
      } else if (['Closed', 'Liquidated', 'Canceled', 'Cancelled'].includes(status)) {
        if (idx !== -1) state.openTrades.splice(idx, 1);
        if (['Closed', 'Liquidated'].includes(status)) {
          state.tradeHistory.unshift({
            _id: trade_id,
            trading_pair_symbol: symbol,
            status,
            close_price,
            pnl,
          });
        }
      }
    },
    setError: (state, action) => { state.error = action.payload; },
    setSelectedCoin: (state, action) => { state.selectedCoin = action.payload; },
    clearMessages: (state) => { state.error = null; state.successMessage = null; },
  },
  extraReducers: (builder) => {
    builder
      // create
      .addCase(createFutureTrade.pending, (state) => {
        state.openOrdersLoading = true;
        state.error = null; state.successMessage = null;
      })
      .addCase(createFutureTrade.fulfilled, (state, action) => {
        state.openOrdersLoading = false;
        const trade = action.payload?.trade;
        if (trade) state.openTrades.push(trade);
        state.successMessage = 'Future trade created successfully';
      })
      .addCase(createFutureTrade.rejected, (state, action) => {
        state.openOrdersLoading = false;
        state.error = action.payload;
      })

      // close
      .addCase(closeFutureTrade.pending, (state) => {
        state.openOrdersLoading = true;
        state.error = null; state.successMessage = null;
      })
      .addCase(closeFutureTrade.fulfilled, (state, action) => {
        state.openOrdersLoading = false;
        const closed = action.payload;
        const idx = state.openTrades.findIndex((t) => t._id === closed._id);
        if (idx !== -1) state.openTrades.splice(idx, 1);
        state.tradeHistory.unshift(closed);
        state.successMessage = 'Future trade closed successfully';
      })
      .addCase(closeFutureTrade.rejected, (state, action) => {
        state.openOrdersLoading = false;
        state.error = action.payload;
      })

      // cancel
      .addCase(cancelFutureTrade.pending, (state) => {
        state.openOrdersLoading = true;
        state.error = null; state.successMessage = null;
      })
      .addCase(cancelFutureTrade.fulfilled, (state, action) => {
        state.openOrdersLoading = false;
        const { trade_id } = action.payload || {};
        state.openTrades = state.openTrades.filter((t) => t._id !== trade_id);
        state.successMessage = 'Limit order canceled';
      })
      .addCase(cancelFutureTrade.rejected, (state, action) => {
        state.openOrdersLoading = false;
        state.error = action.payload;
      })

      // open list
      .addCase(fetchOpenFutureTrades.pending, (state) => {
        state.openOrdersLoading = true; state.error = null;
      })
      .addCase(fetchOpenFutureTrades.fulfilled, (state, action) => {
        state.openTrades = action.payload; state.openOrdersLoading = false;
      })
      .addCase(fetchOpenFutureTrades.rejected, (state, action) => {
        state.openOrdersLoading = false; state.error = action.payload;
      })

      // history
      .addCase(fetchFutureTradeHistory.pending, (state) => {
        state.tradeHistoryLoading = true; state.error = null;
      })
      .addCase(fetchFutureTradeHistory.fulfilled, (state, action) => {
        state.tradeHistory = action.payload; state.tradeHistoryLoading = false;
      })
      .addCase(fetchFutureTradeHistory.rejected, (state, action) => {
        state.tradeHistoryLoading = false; state.error = action.payload;
      });
  },
});

export const { updateOrderStatus, setError, setSelectedCoin, clearMessages } = futuresSlice.actions;
export default futuresSlice.reducer;
